import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import DocumentCategoryCard from './components/DocumentCategoryCard';
import DocumentGenerationPanel from './components/DocumentGenerationPanel';
import DocumentStorageArea from './components/DocumentStorageArea';
import SignatureWorkflowPanel from './components/SignatureWorkflowPanel';
import TemplateManagementSection from './components/TemplateManagementSection';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const DocumentManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { currentLanguage, t } = useLanguage();
  const [isGenerationPanelOpen, setIsGenerationPanelOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const mockUser = {
    name: 'Mohammed Tazi',
    role: 'agency_admin',
    agency: 'Agence Al-Baraka Travel',
    avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
  };

  const mockNotifications = [
    {
      id: 'notif-001',
      title: t('documents.newDocumentSigned'),
      message: t('documents.newDocumentSignedDesc'),
      timestamp: new Date(Date.now() - 1800000),
      read: false,
      priority: 'high'
    },
    {
      id: 'notif-002',
      title: t('documents.signaturePending'),
      message: t('documents.signaturePendingDesc'),
      timestamp: new Date(Date.now() - 3600000),
      read: false,
      priority: 'medium'
    }
  ];

  const documentCategories = [
    {
      id: 'contracts',
      name: { fr: t('documents.contracts'), ar: t('documents.contracts') },
      description: { fr: t('documents.contractsDesc'), ar: t('documents.contractsDesc') },
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      total: 245,
      generated: 198,
      pending: 47,
      completionRate: 81
    },
    {
      id: 'official_lists',
      name: { fr: t('documents.officialLists'), ar: t('documents.officialLists') },
      description: { fr: t('documents.officialListsDesc'), ar: t('documents.officialListsDesc') },
      icon: 'List',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      total: 12,
      generated: 10,
      pending: 2,
      completionRate: 83
    },
    {
      id: 'convocations',
      name: { fr: t('documents.convocations'), ar: t('documents.convocations') },
      description: { fr: t('documents.convocationsDesc'), ar: t('documents.convocationsDesc') },
      icon: 'Bell',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      total: 245,
      generated: 156,
      pending: 89,
      completionRate: 64
    },
    {
      id: 'attestations',
      name: { fr: t('documents.attestations'), ar: t('documents.attestations') },
      description: { fr: t('documents.attestationsDesc'), ar: t('documents.attestationsDesc') },
      icon: 'Award',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      total: 189,
      generated: 145,
      pending: 44,
      completionRate: 77
    },
    {
      id: 'invoices',
      name: { fr: t('documents.invoices'), ar: t('documents.invoices') },
      description: { fr: t('documents.invoicesDesc'), ar: t('documents.invoicesDesc') },
      icon: 'Receipt',
      color: 'text-success',
      bgColor: 'bg-success/10',
      total: 312,
      generated: 298,
      pending: 14,
      completionRate: 96
    }
  ];

  const tabs = [
    { id: 'overview', label: { fr: t('documents.overview'), ar: t('documents.overview') }, icon: 'LayoutGrid' },
    { id: 'storage', label: { fr: t('documents.storage'), ar: t('documents.storage') }, icon: 'HardDrive' },
    { id: 'signatures', label: { fr: t('documents.signatures'), ar: t('documents.signatures') }, icon: 'PenTool' },
    { id: 'templates', label: { fr: t('documents.templates'), ar: t('documents.templates') }, icon: 'FileType' }
  ];

  const handleGenerateClick = (category) => {
    setSelectedCategory(category);
    setIsGenerationPanelOpen(true);
  };

  const handleGenerate = (generationData) => {
    console.log('Generating documents:', generationData);
    
    // Provide user feedback
    const categoryName = documentCategories?.find(c => c?.id === generationData?.category)?.name?.[currentLanguage];
    alert(`${t('documents.generateSuccess')}: ${categoryName}`);
  };

  const handleLanguageChange = (newLanguage) => {
    console.log('Language changed to:', newLanguage);
  };

  const handleLogout = () => {
    console.log('Logging out...');
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className={`transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'
        }`}
      >
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-semibold text-foreground">
                {t('documents.title')}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t('documents.subtitle')}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <NotificationCenter
                notifications={mockNotifications}
                currentLanguage={currentLanguage}
                onNotificationClick={(notif) => console.log('Notification clicked:', notif)}
                onMarkAsRead={(notifId) => console.log('Mark as read:', notifId)}
              />
              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                onLanguageChange={handleLanguageChange}
                currentLanguage={currentLanguage}
              />
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border border-border rounded-lg p-4 md:p-6 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="FileStack" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-1">
                    {t('documents.centerTitle')}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t('documents.centerSubtitle')}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg">
                  <Icon name="FileCheck" size={18} className="text-success" />
                  <div>
                    <p className="data-text font-semibold text-foreground">807</p>
                    <p className="caption text-muted-foreground">
                      {t('documents.generated')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg">
                  <Icon name="Clock" size={18} className="text-warning" />
                  <div>
                    <p className="data-text font-semibold text-foreground">196</p>
                    <p className="caption text-muted-foreground">
                      {t('documents.pending')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-smooth
                    ${activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-warm'
                      : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label?.[currentLanguage]}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {documentCategories?.map(category => (
                <DocumentCategoryCard
                  key={category?.id}
                  category={category}
                  currentLanguage={currentLanguage}
                  onGenerateClick={handleGenerateClick}
                />
              ))}
            </div>
          )}

          {activeTab === 'storage' && (
            <DocumentStorageArea currentLanguage={currentLanguage} />
          )}

          {activeTab === 'signatures' && (
            <SignatureWorkflowPanel currentLanguage={currentLanguage} />
          )}

          {activeTab === 'templates' && (
            <TemplateManagementSection currentLanguage={currentLanguage} />
          )}
        </main>

        <footer className="border-t border-border bg-card mt-12">
          <div className="px-4 md:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                &copy; {new Date()?.getFullYear()} OmraHajjManager.{' '}
                {currentLanguage === 'fr' ?'Tous droits réservés.' :'جميع الحقوق محفوظة.'}
              </p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <a href="#" className="hover:text-primary transition-smooth">
                  {currentLanguage === 'fr' ? 'Confidentialité' : 'الخصوصية'}
                </a>
                <a href="#" className="hover:text-primary transition-smooth">
                  {currentLanguage === 'fr' ? 'Conditions' : 'الشروط'}
                </a>
                <a href="#" className="hover:text-primary transition-smooth">
                  {currentLanguage === 'fr' ? 'Support' : 'الدعم'}
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <DocumentGenerationPanel
        isOpen={isGenerationPanelOpen}
        onClose={() => setIsGenerationPanelOpen(false)}
        category={selectedCategory}
        currentLanguage={currentLanguage}
        onGenerate={handleGenerate}
      />
    </div>
  );
};

export default DocumentManagement;